import React from 'react'
import cx from 'classnames'
import styles from './Dialog.sass'
import Button, { ButtonProps } from '../Button'
import FullscreenOverlay from 'components/FullscreenOverlay'
import Icon from 'components/Icon'
import { useOnClickOutside } from 'hooks/click-outside'
import { useModalStack } from '../../hooks/modal-stack'
import { animated } from 'react-spring'
import { useCssTransition } from '../../hooks/transitioning'
import { observer } from 'mobx-react'

interface DialogProps {
  children?: React.ReactNode
  className?: string
  showing: boolean
  onClose?: (() => unknown) | null | false
  disableCloseOnOutsideClick?: boolean
  disableCloseOnEsc?: boolean
}

/**
 * Dialog - NOT UP TO DATE WITH DESIGN SPEC.
 * USE ModalSheet COMPONENT INSTEAD.
 */
export const Dialog = observer(function Dialog({
  children,
  className,
  onClose,
  showing,
  disableCloseOnOutsideClick,
  disableCloseOnEsc,
}: DialogProps) {
  const dialogRef = React.useRef<HTMLDivElement | null>(null)
  const close = React.useCallback(() => onClose && onClose(), [onClose])
  const onOutsideClick = React.useCallback(
    () => !disableCloseOnOutsideClick && close(),
    [disableCloseOnOutsideClick, close],
  )

  const { isFirstResponder, stackDepthStyle } = useModalStack(
    { showing, close },
    {
      disableCloseOnEsc,
    },
  )

  useOnClickOutside(dialogRef, isFirstResponder ? onOutsideClick : undefined)
  const [transitionRef, rendering, hidden] = useCssTransition(showing)
  return (
    rendering && (
      <FullscreenOverlay showing={!hidden} ref={transitionRef}>
        <div className={styles.container}>
          <animated.div
            className={cx(styles.dialog, className, hidden && styles.hidden)}
            ref={dialogRef}
            style={!hidden ? stackDepthStyle : undefined}
          >
            {onClose ? (
              <a className={styles.closeBtn} onClick={close}>
                <Icon name="close" />
              </a>
            ) : null}
            {children}
          </animated.div>
        </div>
      </FullscreenOverlay>
    )
  )
})

// Dialog Header
interface DialogHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  bordered?: boolean
}

export const DialogHeader = ({
  children,
  className,
  bordered,
  ...rest
}: DialogHeaderProps) => (
  <div
    className={cx(styles.dialogHeader, className, bordered && styles.bordered)}
    {...rest}
  >
    {children}
  </div>
)

// Dialog Title
interface DialogTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {}

export const DialogTitle = ({ children, className }: DialogTitleProps) => (
  <h3 className={cx(styles.dialogTitle, className)}>{children}</h3>
)

// Dialog Description
interface DialogDescriptionProps
  extends React.HTMLAttributes<HTMLParagraphElement> {}

export const DialogDescription = ({
  children,
  className,
}: DialogDescriptionProps) => (
  <p className={cx(styles.dialogDescription, className)}>{children}</p>
)

// Dialog Illustration
interface DialogIllustrationProps {
  src?: string
  alt?: string
  children?: React.ReactNode
}

export const DialogIllustration = ({
  children,
  src,
  alt,
}: DialogIllustrationProps) => (
  <div className={cx(styles.dialogIllustration)}>
    {src ? (
      <img className={styles.dialogIllustrationImage} src={src} alt={alt} />
    ) : (
      children
    )}
  </div>
)

// Dialog Content
interface DialogContentProps extends React.HTMLAttributes<HTMLDivElement> {}

export const DialogContent = ({ children, className }: DialogContentProps) => (
  <div className={cx(styles.dialogContent, className)}>{children}</div>
)

// Dialog Footer
interface DialogFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  bordered?: boolean
}

export const DialogFooter = ({
  bordered,
  children,
  className,
}: DialogFooterProps) => (
  <div
    className={cx(styles.dialogFooter, bordered && styles.bordered, className)}
  >
    {children}
  </div>
)

interface DialogButtonProps extends ButtonProps {}

export const DialogButton = ({ children, ...other }: DialogButtonProps) => (
  <div className={styles.dialogButton}>
    <Button {...other}>{children}</Button>
  </div>
)

export const DialogButtonGroup = ({
  children,
}: {
  children: React.ReactNode
}) => <div className={styles.dialogButtonGroup}>{children}</div>

export const DialogFooterSpacer = () => (
  <div className={styles.dialogFooterSpacer}></div>
)
